<template>
  <div class="layout-page">
    <div id="large-screen" class="layout-fit">
      <main class="layout-app">
        <div v-if="isCountingDown" class="countdown-overlay">
          <div class="countdown-box">
            <div class="countdown-circle"><span>{{ countdown }}s</span></div>
            <div class="countdown-text">设置中，请稍候...</div>
          </div>
        </div>

        <div class="current-time">{{ currentTime }}</div>
        <button class="settings-trigger" type="button" aria-label="设置" @click="openSettings"></button>
        <button class="logout-trigger" type="button" aria-label="退出" @click="logout"></button>

        <device-runtime-panel :protocol="protocol" />
        <device-info-panel
          :protocol="protocol"
          :refreshing="isRefreshingProtocol"
          :refresh-countdown="refreshCountdown"
          @refresh="refreshProtocol"
        />
        <water-usage-charts :protocol="protocol" />
        <consumables-panel :protocol="protocol" />
        <alert-panel :protocol="protocol" />
      </main>
    </div>

    <device-settings-dialog
      v-model="settingsVisible"
      :protocol="protocol"
      :editable="canEditSettings"
      @command="requestCommandConfirmation"
    />

    <host-password-dialog
      v-model="passwordDialogVisible"
      :expected-password="userPassword"
      @confirm="confirmHostPassword"
      @closed="handleHostPasswordDialogClosed"
    />

    <protocol-command-confirm-dialog
      v-model="commandConfirmationVisible"
      :command="pendingCommand"
      :submitting="isSubmittingCommand"
      @confirm="confirmCommand"
    />
  </div>
</template>

<script>
import AlertPanel from "../components/device-dashboard/AlertPanel.vue";
import autofit from "autofit.js";
import ConsumablesPanel from "../components/device-dashboard/ConsumablesPanel.vue";
import DeviceInfoPanel from "../components/device-dashboard/DeviceInfoPanel.vue";
import DeviceRuntimePanel from "../components/device-dashboard/DeviceRuntimePanel.vue";
import DeviceSettingsDialog from "../components/device-dashboard/DeviceSettingsDialog.vue";
import HostPasswordDialog from "../components/device-dashboard/HostPasswordDialog.vue";
import ProtocolCommandConfirmDialog from "../components/device-dashboard/ProtocolCommandConfirmDialog.vue";
import WaterUsageCharts from "../components/device-dashboard/WaterUsageCharts.vue";
import { validateProtocolCommand } from "../utils/protocol/commands";
import { mapState } from "vuex";

const POLL_INTERVAL = 180000;
const COUNTDOWN_SECONDS = 30;

export default {
  name: "Layout",
  components: {
    AlertPanel,
    ConsumablesPanel,
    DeviceInfoPanel,
    DeviceRuntimePanel,
    DeviceSettingsDialog,
    HostPasswordDialog,
    ProtocolCommandConfirmDialog,
    WaterUsageCharts
  },
  data() {
    return {
      protocol: {},
      currentTime: "",
      settingsVisible: false,
      passwordDialogVisible: false,
      passwordDialogPurpose: null,
      commandConfirmationVisible: false,
      pendingCommand: null,
      isSubmittingCommand: false,
      isCountingDown: false,
      countdown: COUNTDOWN_SECONDS,
      isRefreshingProtocol: false,
      hasSettingsPermission: false,
      isCheckingSettingsPermission: false,
      refreshCountdown: COUNTDOWN_SECONDS,
      pollTimer: null,
      timeTimer: null,
      countdownTimer: null,
      refreshCountdownTimer: null
    };
  },
  computed: {
    ...mapState({
      userInfo: state => state.deviceSession.username,
      userPassword: state => state.deviceSession.password,
      userSn: state => state.deviceSession.serialNumber,
      accessToken: state => state.accessToken
    }),
    hasCompleteDeviceIdentity() {
      return [
        this.userInfo,
        this.userSn,
        this.protocol.productKey,
        this.protocol.deviceName
      ].every(value => typeof value === "string" && value.trim());
    },
    canEditSettings() {
      return this.hasSettingsPermission && this.hasCompleteDeviceIdentity;
    },
    isDeviceOffline() {
      return String(this.protocol.status || "").toLowerCase() === "offline";
    }
  },
  watch: {
    settingsVisible(visible) {
      if (visible) {
        this.stopPolling();
      } else if (!this.isCountingDown && !this.isRefreshingProtocol) {
        this.startPolling();
      }
    },
    commandConfirmationVisible(visible) {
      if (!visible && !this.isSubmittingCommand) {
        this.pendingCommand = null;
      }
    }
  },
  mounted() {
    this.initAutofit();
    this.getProtocol();
    this.startPolling();
    this.startClock();
  },
  beforeDestroy() {
    if (autofit.isAutofitRunning) {
      autofit.off();
    }
    this.stopPolling();
    if (this.timeTimer) {
      clearInterval(this.timeTimer);
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
    if (this.refreshCountdownTimer) {
      clearInterval(this.refreshCountdownTimer);
    }
  },
  methods: {
    initAutofit() {
      this.$nextTick(() => {
        autofit.init(
          {
            el: "#large-screen",
            dw: 1920,
            dh: 1080,
            resize: true,
            delay: 100,
            limit: 0,
            cssMode: "scale"
          },
          false
        );
      });
    },
    getProtocol() {
      return this.$api.xueLing
        .getSingleDeviceData({ username: this.userInfo, s_no: this.userSn })
        .then(response => {
          this.protocol = response || {};
        })
        .catch(error => {
          // 保留上一份有效协议，避免请求波动导致整页回退为空状态。
          // eslint-disable-next-line no-console
          console.error("获取设备协议失败:", error);
        });
    },
    refreshProtocol() {
      if (this.isRefreshingProtocol) {
        this.$message.error("数据更新中，请稍候");
        return;
      }

      if (this.isDeviceOffline) {
        this.$message.warning("设备已离线，暂不能更新数据");
        return;
      }

      const refreshFields = [
        this.userInfo,
        this.userPassword,
        this.protocol.productKey,
        this.protocol.deviceName
      ];
      if (!refreshFields.every(value => typeof value === "string" && value.trim())) {
        this.$message.error("设备身份信息尚未完整加载，暂不能更新数据");
        return;
      }

      this.submitRefreshCommand(this.userPassword);
    },
    submitRefreshCommand(password) {
      this.stopPolling();
      this.isRefreshingProtocol = true;
      this.refreshCountdown = COUNTDOWN_SECONDS;

      if (this.refreshCountdownTimer) {
        clearInterval(this.refreshCountdownTimer);
        this.refreshCountdownTimer = null;
      }

      this.$api.xueLing
        .refreshDeviceData({
          username: this.userInfo,
          password,
          ProductKey: this.protocol.productKey,
          DeviceName: this.protocol.deviceName
        })
        .then(response => {
          if (!response || response.Success !== true) {
            throw new Error(this.getDeviceCommandError(response));
          }

          this.startRefreshCountdown();
        })
        .catch(error => {
          this.isRefreshingProtocol = false;
          this.refreshCountdown = COUNTDOWN_SECONDS;
          this.$message.error(
            `更新数据指令下发失败: ${error.message || "请稍后重试"}`
          );
          this.startPolling();
          // eslint-disable-next-line no-console
          console.error("更新数据指令下发失败:", error);
        });
    },
    startRefreshCountdown() {
      this.refreshCountdownTimer = setInterval(() => {
        this.refreshCountdown -= 1;
        if (this.refreshCountdown > 0) {
          return;
        }

        clearInterval(this.refreshCountdownTimer);
        this.refreshCountdownTimer = null;
        this.refreshCountdown = 0;

        this.getProtocol().finally(() => {
          this.isRefreshingProtocol = false;
          this.refreshCountdown = COUNTDOWN_SECONDS;
          if (!this.settingsVisible && !this.isCountingDown) {
            this.startPolling();
          }
        });
      }, 1000);
    },
    startPolling() {
      this.stopPolling();
      if (this.settingsVisible || this.isCountingDown || this.isRefreshingProtocol) {
        return;
      }
      this.pollTimer = setInterval(() => this.getProtocol(), POLL_INTERVAL);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    startClock() {
      const updateClock = () => {
        const now = new Date();
        const weekDays = [
          "星期日",
          "星期一",
          "星期二",
          "星期三",
          "星期四",
          "星期五",
          "星期六"
        ];
        const pad = value => String(value).padStart(2, "0");
        this.currentTime = `${weekDays[now.getDay()]} ${now.getFullYear()}-${pad(
          now.getMonth() + 1
        )}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(
          now.getMinutes()
        )}:${pad(now.getSeconds())}`;
      };

      updateClock();
      this.timeTimer = setInterval(updateClock, 1000);
    },
    checkSettingsPermission() {
      this.hasSettingsPermission = false;
      if (!this.accessToken) {
        return Promise.resolve(false);
      }

      this.isCheckingSettingsPermission = true;
      return this.$api.hostScreenPermission
        .getCurrentUserEditPermission()
        .then(response => {
          this.hasSettingsPermission = Boolean(
            response && response.data === true
          );
          return this.hasSettingsPermission;
        })
        .catch(() => {
          this.hasSettingsPermission = false;
          return false;
        })
        .finally(() => {
          this.isCheckingSettingsPermission = false;
        });
    },
    openSettings() {
      if (this.isCheckingSettingsPermission) {
        return;
      }

      this.checkSettingsPermission().then(canEdit => {
        if (!canEdit) {
          this.$message.warning("当前账号暂无参数修改权限，设置内容仅可查看");
        }
        this.settingsVisible = true;
      });
    },
    hasRequiredWriteFields() {
      return this.hasCompleteDeviceIdentity;
    },
    requestCommandConfirmation({ ms, successMessage }) {
      if (this.isSubmittingCommand) {
        this.$message.error("指令正在下发，请稍候");
        return;
      }

      if (!this.hasSettingsPermission) {
        this.$message.error("当前账号暂无参数修改权限");
        return;
      }

      if (!this.hasRequiredWriteFields()) {
        this.$message.error("设备身份信息尚未完整加载，暂不能下发协议");
        return;
      }

      try {
        this.pendingCommand = {
          ms: validateProtocolCommand(ms),
          successMessage
        };
        this.openHostPasswordDialog("command");
      } catch (error) {
        this.$message.error(error.message || "协议转换失败");
      }
    },
    openHostPasswordDialog(purpose) {
      if (this.passwordDialogVisible) {
        return;
      }

      this.passwordDialogPurpose = purpose;
      this.passwordDialogVisible = true;
    },
    confirmHostPassword(password) {
      const purpose = this.passwordDialogPurpose;
      this.passwordDialogVisible = false;
      this.passwordDialogPurpose = null;

      if (purpose === "command" && this.pendingCommand) {
        this.pendingCommand = {
          ...this.pendingCommand,
          password
        };
        this.commandConfirmationVisible = true;
        return;
      }

    },
    handleHostPasswordDialogClosed() {
      if (this.passwordDialogPurpose === "command") {
        this.pendingCommand = null;
      }

      this.passwordDialogPurpose = null;
    },
    confirmCommand(command) {
      if (
        !command ||
        !command.ms ||
        !command.password ||
        this.isSubmittingCommand
      ) {
        return;
      }

      this.commandConfirmationVisible = false;
      this.submitCommand(command);
    },
    getDeviceCommandError(response) {
      if (response === "noPermission") {
        return "当前账号没有该设备的读写权限";
      }

      if (response && typeof response === "object") {
        if (response.Code === "iot.messagebroker.OFFLINE") {
          return "设备离线，无法下发指令";
        }

        if (response.Code === "iot.device.NotExistedDevice") {
          return "设备不存在，请核对设备三元素";
        }

        return (
          response.ErrorMessage ||
          response.message ||
          response.Code ||
          "设备未确认接收指令"
        );
      }

      return "设备未确认接收指令";
    },
    submitCommand({ ms, successMessage, password }) {
      if (this.isSubmittingCommand) {
        return;
      }

      if (!this.hasSettingsPermission) {
        this.$message.error("当前账号暂无参数修改权限");
        return;
      }

      if (!this.hasRequiredWriteFields()) {
        this.$message.error("设备身份信息尚未完整加载，暂不能下发协议");
        return;
      }

      if (typeof password !== "string" || !password.trim()) {
        this.$message.error("请输入主机密码");
        return;
      }

      this.isSubmittingCommand = true;
      this.$api.xueLing
        .setSingleDeviceData({
          username: this.userInfo,
          s_no: this.userSn,
          pk: this.protocol.productKey,
          dn: this.protocol.deviceName,
          password,
          ms
        })
        .then(response => {
          if (!response || response.Success !== true) {
            throw new Error(this.getDeviceCommandError(response));
          }

          this.$message.success(successMessage);
          this.startCountdown();
        })
        .catch(error => {
          this.$message.error(`设置失败: ${error.message || "请稍后重试"}`);
          // eslint-disable-next-line no-console
          console.error("提交设备设置失败:", error);
        })
        .finally(() => {
          this.isSubmittingCommand = false;
          this.pendingCommand = null;
        });
    },
    startCountdown() {
      this.stopPolling();
      this.isCountingDown = true;
      this.settingsVisible = false;
      this.countdown = COUNTDOWN_SECONDS;

      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
      }

      this.countdownTimer = setInterval(() => {
        this.countdown -= 1;
        if (this.countdown > 0) {
          return;
        }

        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
        this.isCountingDown = false;
        this.countdown = COUNTDOWN_SECONDS;
        this.getProtocol();
        this.startPolling();
      }, 1000);
    },
    logout() {
      this.$store.commit("clearVisualizationAccessToken");
      this.$store.commit("clearDeviceSession");
      this.$router.push("/Login");
    }
  }
};
</script>

<style lang="scss" scoped>
.layout-page {
  width: 100%;
  height: 100%;
  background: #000;
}

.layout-fit {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
}

.layout-app {
  position: relative;
  width: 1920px;
  height: 1080px;
  background: url("/images/dashboard/jsht.png") no-repeat center / 100% 100%;
  color: #fff;
}

.current-time {
  position: absolute;
  top: 28px;
  left: 52px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 18px;
  font-weight: 400;
}

.settings-trigger,
.logout-trigger {
  position: absolute;
  border: 0;
  background-color: transparent;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;
  cursor: pointer;
}

.settings-trigger {
  top: 23px;
  right: 135px;
  width: 34px;
  height: 34px;
  background-image: url("/images/dashboard/shezhi.png");
}

.logout-trigger {
  top: 26px;
  right: 90px;
  width: 28px;
  height: 28px;
  background-image: url("/images/dashboard/tuichu.png");
}

.countdown-overlay {
  position: fixed;
  z-index: 9999;
  top: 0;
  left: 0;
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
}

.countdown-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.countdown-circle {
  display: flex;
  width: 120px;
  height: 120px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(135deg, #3fafff 0%, #438eff 100%);
  box-shadow: 0 4px 12px rgba(67, 142, 255, 0.5);
}

.countdown-circle span {
  color: #fff;
  font-size: 36px;
  font-weight: 700;
}

.countdown-text {
  margin-top: 20px;
  color: #fff;
  font-size: 18px;
}
</style>
